import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth, API_BASE } from '@/context/AuthContext';
import AppHeader from '@/components/AppHeader';
import {
  Users, FileText, IndianRupee, CheckCircle2, Clock, TrendingUp, TrendingDown,
  Stethoscope, Megaphone, Activity, ArrowUpRight, ArrowRight, RefreshCw,
  Building2, ChevronRight, Sparkles, PieChart as PieIcon,
} from 'lucide-react';

const fmtINR = (n) => {
  const v = Number(n || 0);
  return `₹${v.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return iso; }
};

// Small colored KPI tile
const KpiTile = ({ label, value, sub, icon: Icon, accent = 'teal', delta, testid }) => {
  const accents = {
    teal:   'from-teal-500/20 to-teal-500/5 border-teal-500/30 text-teal-300',
    emerald:'from-emerald-500/20 to-emerald-500/5 border-emerald-500/30 text-emerald-300',
    amber:  'from-amber-500/20 to-amber-500/5 border-amber-500/30 text-amber-300',
    rose:   'from-rose-500/20 to-rose-500/5 border-rose-500/30 text-rose-300',
    sky:    'from-sky-500/20 to-sky-500/5 border-sky-500/30 text-sky-300',
    indigo: 'from-indigo-500/20 to-indigo-500/5 border-indigo-500/30 text-indigo-300',
    fuchsia:'from-fuchsia-500/20 to-fuchsia-500/5 border-fuchsia-500/30 text-fuchsia-300',
  };
  const showDelta = typeof delta === 'number' && !isNaN(delta);
  const positive = showDelta && delta >= 0;
  return (
    <div
      data-testid={testid}
      className={`relative overflow-hidden rounded-2xl border bg-gradient-to-br ${accents[accent] || accents.teal} p-5 group hover:scale-[1.01] transition-transform`}
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[11px] uppercase tracking-widest font-semibold text-slate-400">{label}</p>
          <p className="mt-2 text-2xl sm:text-3xl font-bold text-white">{value}</p>
          {sub && <p className="mt-1 text-xs text-slate-400">{sub}</p>}
        </div>
        <div className="p-2.5 rounded-xl bg-slate-900/60 border border-slate-800">
          <Icon className="w-5 h-5" />
        </div>
      </div>
      {showDelta && (
        <div className={`mt-3 inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-md ${positive ? 'text-emerald-300 bg-emerald-500/10 border border-emerald-500/20' : 'text-rose-300 bg-rose-500/10 border border-rose-500/20'}`}>
          {positive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
          {positive ? '+' : ''}{delta}% vs prev
        </div>
      )}
    </div>
  );
};

// Mini trend chart (inline SVG bar chart)
const TrendChart = ({ trend }) => {
  const max = Math.max(1, ...trend.map(t => t.amount));
  return (
    <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-5" data-testid="dashboard-trend-chart">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4 text-teal-400" />
          <h3 className="text-sm font-semibold text-white">Last 7 Days — Referral Revenue</h3>
        </div>
        <span className="text-[10px] font-mono text-slate-500">₹ per day</span>
      </div>
      <div className="flex items-end gap-2 h-40">
        {trend.map((d, idx) => {
          const h = (d.amount / max) * 100;
          const isToday = idx === trend.length - 1;
          return (
            <div key={d.date} className="flex-1 flex flex-col items-center gap-2 group">
              <div className="w-full flex flex-col items-center justify-end flex-1 relative">
                <div
                  className={`w-full rounded-t-md transition-all ${isToday ? 'bg-gradient-to-t from-teal-500 to-emerald-400' : 'bg-slate-700 group-hover:bg-teal-600/70'}`}
                  style={{ height: `${Math.max(2, h)}%` }}
                />
                <div className="absolute -top-8 opacity-0 group-hover:opacity-100 transition-opacity bg-slate-950 border border-slate-700 rounded-md px-2 py-0.5 text-[10px] whitespace-nowrap text-white pointer-events-none z-10">
                  {fmtINR(d.amount)} · {d.count} ref
                </div>
              </div>
              <span className={`text-[10px] font-mono ${isToday ? 'text-teal-300 font-semibold' : 'text-slate-500'}`}>{d.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Section revenue donut (simple stacked bar)
const SectionSplit = ({ sections }) => {
  const total = (sections.scan || 0) + (sections.lab || 0) + (sections.pharmacy || 0) + (sections.ip || 0);
  const items = [
    { key: 'scan',     label: 'Scan',     val: sections.scan || 0,     color: 'bg-sky-500',     text: 'text-sky-300' },
    { key: 'lab',      label: 'Lab',      val: sections.lab || 0,      color: 'bg-fuchsia-500', text: 'text-fuchsia-300' },
    { key: 'pharmacy', label: 'Pharmacy', val: sections.pharmacy || 0, color: 'bg-emerald-500', text: 'text-emerald-300' },
    { key: 'ip',       label: 'IP',       val: sections.ip || 0,       color: 'bg-amber-500',   text: 'text-amber-300' },
  ];
  return (
    <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-5" data-testid="dashboard-section-split">
      <div className="flex items-center gap-2 mb-4">
        <PieIcon className="w-4 h-4 text-teal-400" />
        <h3 className="text-sm font-semibold text-white">MTD Revenue by Section</h3>
      </div>
      {total <= 0 ? (
        <p className="text-xs text-slate-500 py-6 text-center">No revenue recorded this month yet.</p>
      ) : (
        <>
          <div className="flex h-3 rounded-full overflow-hidden bg-slate-800/60">
            {items.map(it => it.val > 0 && (
              <div key={it.key} className={it.color} style={{ width: `${(it.val / total) * 100}%` }} title={`${it.label}: ${fmtINR(it.val)}`} />
            ))}
          </div>
          <div className="mt-4 grid grid-cols-2 gap-2">
            {items.map(it => (
              <div key={it.key} className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-2">
                  <span className={`w-2.5 h-2.5 rounded-full ${it.color}`} />
                  <span className="text-slate-300">{it.label}</span>
                </div>
                <span className={`font-semibold ${it.text}`}>{fmtINR(it.val)}</span>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-3 border-t border-slate-800 flex items-center justify-between">
            <span className="text-[11px] uppercase tracking-wider text-slate-500">Total</span>
            <span className="text-sm font-bold text-white">{fmtINR(total)}</span>
          </div>
        </>
      )}
    </div>
  );
};

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const { getAuthHeader, user, isManagement } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    try {
      setError('');
      const res = await axios.get(`${API_BASE}/dashboard/admin`, { headers: getAuthHeader() });
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load dashboard data.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const handleRefresh = () => { setRefreshing(true); load(); };

  const kpis = data?.kpis;
  const paidPct = useMemo(() => {
    const t = kpis?.mtd?.amount || 0;
    if (t <= 0) return 0;
    return Math.round(((kpis.mtd.paid_amount || 0) / t) * 100);
  }, [kpis]);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <AppHeader />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Title */}
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-teal-400 font-semibold">
              <Sparkles className="w-3.5 h-3.5" />
              {isManagement ? 'Management Overview' : 'Admin Command Center'}
            </div>
            <h1 data-testid="dashboard-page-title" className="mt-1 text-3xl sm:text-4xl font-bold text-white tracking-tight">
              Welcome back, {user?.name?.split(' ')[0] || 'there'}.
            </h1>
            <p className="text-sm text-slate-400 mt-1">
              A live pulse of referrals, payments and top contributors.
              {data?.ist_today && <span className="text-slate-500"> · IST {data.ist_today}</span>}
            </p>
          </div>
          <button
            onClick={handleRefresh}
            data-testid="dashboard-refresh-btn"
            disabled={refreshing}
            className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-slate-900 border border-slate-800 hover:border-teal-600/60 text-xs text-slate-200 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {loading && (
          <div className="py-24 text-center text-slate-400 text-sm" data-testid="dashboard-loading">Loading dashboard…</div>
        )}
        {error && !loading && (
          <div className="p-4 rounded-xl bg-rose-950/40 border border-rose-800/60 text-rose-200 text-sm" data-testid="dashboard-error">{error}</div>
        )}

        {!loading && !error && data && (
          <>
            {/* KPI Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="dashboard-kpi-grid">
              <KpiTile
                testid="kpi-today"
                label="Today's Referrals"
                value={kpis.today.count}
                sub={fmtINR(kpis.today.amount)}
                icon={FileText}
                accent="teal"
                delta={kpis.today_vs_yesterday_pct}
              />
              <KpiTile
                testid="kpi-mtd"
                label="Month to Date"
                value={kpis.mtd.count}
                sub={fmtINR(kpis.mtd.amount)}
                icon={IndianRupee}
                accent="emerald"
                delta={kpis.mtd_vs_last_month_pct}
              />
              <KpiTile
                testid="kpi-paid"
                label="Paid (MTD)"
                value={fmtINR(kpis.mtd.paid_amount)}
                sub={`${kpis.mtd.paid_count} cleared · ${paidPct}% of MTD`}
                icon={CheckCircle2}
                accent="sky"
              />
              <KpiTile
                testid="kpi-pending"
                label="Pending (MTD)"
                value={fmtINR(kpis.mtd.pending_amount)}
                sub={`${kpis.mtd.pending_count} awaiting settlement`}
                icon={Clock}
                accent="amber"
              />
            </div>

            {/* Row 2: Trend + Section split */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div className="lg:col-span-2">
                <TrendChart trend={data.trend_7d} />
              </div>
              <SectionSplit sections={data.section_revenue_mtd} />
            </div>

            {/* Row 3: Top Doctors + Top Executives */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-5" data-testid="dashboard-top-doctors">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Stethoscope className="w-4 h-4 text-teal-400" />
                    <h3 className="text-sm font-semibold text-white">Top Referring Doctors — MTD</h3>
                  </div>
                  <span className="text-[10px] text-slate-500">by revenue</span>
                </div>
                {data.top_doctors_mtd.length === 0 ? (
                  <p className="py-8 text-center text-xs text-slate-500">No referrals from any doctor this month yet.</p>
                ) : (
                  <ul className="divide-y divide-slate-800/60">
                    {data.top_doctors_mtd.map((d, idx) => (
                      <li key={d.doctor_id} className="py-2.5 flex items-center gap-3">
                        <span className="w-7 h-7 rounded-lg bg-teal-500/10 text-teal-300 border border-teal-500/20 flex items-center justify-center text-xs font-bold">{idx + 1}</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-white truncate">{d.name}</p>
                          <p className="text-[11px] text-slate-500 truncate">{d.village || 'No village'} · {d.count} referral(s)</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-bold text-white">{fmtINR(d.amount)}</p>
                          <p className="text-[10px] text-slate-500">Paid {fmtINR(d.paid_amount)} · Pending {fmtINR(d.pending_amount)}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-5" data-testid="dashboard-top-executives">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Megaphone className="w-4 h-4 text-teal-400" />
                    <h3 className="text-sm font-semibold text-white">Top Marketing Executives — MTD</h3>
                  </div>
                  <span className="text-[10px] text-slate-500">by revenue</span>
                </div>
                {data.top_executives_mtd.length === 0 ? (
                  <p className="py-8 text-center text-xs text-slate-500">No exec-attributed referrals this month yet.</p>
                ) : (
                  <ul className="divide-y divide-slate-800/60">
                    {data.top_executives_mtd.map((e, idx) => (
                      <li key={e.executive_id} className="py-2.5 flex items-center gap-3">
                        <span className="w-7 h-7 rounded-lg bg-indigo-500/10 text-indigo-300 border border-indigo-500/20 flex items-center justify-center text-xs font-bold">{idx + 1}</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-white truncate">{e.name}</p>
                          <p className="text-[11px] text-slate-500 truncate">{e.village_area || 'No area'} · {e.count} referral(s)</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-bold text-white">{fmtINR(e.amount)}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            {/* Row 4: Recent Activity + Directory */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div className="lg:col-span-2 bg-slate-900/60 border border-slate-800 rounded-2xl p-5" data-testid="dashboard-recent-referrals">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-teal-400" />
                    <h3 className="text-sm font-semibold text-white">Recent Referrals</h3>
                  </div>
                  <button
                    onClick={() => navigate('/staff/referrals')}
                    data-testid="dashboard-view-all-referrals-btn"
                    className="text-[11px] font-medium text-teal-400 hover:text-teal-300 inline-flex items-center gap-1"
                  >
                    View all <ArrowRight className="w-3 h-3" />
                  </button>
                </div>
                {data.recent_referrals.length === 0 ? (
                  <p className="py-8 text-center text-xs text-slate-500">No referrals yet.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-left text-slate-500 uppercase tracking-wider border-b border-slate-800">
                          <th className="pb-2 font-medium">Ref ID</th>
                          <th className="pb-2 font-medium">Patient</th>
                          <th className="pb-2 font-medium">Doctor</th>
                          <th className="pb-2 font-medium text-right">Amount</th>
                          <th className="pb-2 font-medium text-right">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-800/60">
                        {data.recent_referrals.map(r => (
                          <tr key={r.id} className="hover:bg-slate-800/30 transition-colors">
                            <td className="py-2 font-mono text-[10px] text-slate-400">{r.referral_id || r.id.slice(0, 8)}</td>
                            <td className="py-2 text-slate-200">{r.patient_name}</td>
                            <td className="py-2 text-slate-400">{r.doctor_name || '—'}</td>
                            <td className="py-2 text-right font-semibold text-white">{fmtINR(r.final_referral_amount)}</td>
                            <td className="py-2 text-right">
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold border ${
                                r.payment_status === 'paid'
                                  ? 'bg-emerald-500/10 text-emerald-300 border-emerald-500/20'
                                  : 'bg-amber-500/10 text-amber-300 border-amber-500/20'
                              }`}>
                                {r.payment_status === 'paid' ? <CheckCircle2 className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                                {r.payment_status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-5" data-testid="dashboard-directory-counts">
                <div className="flex items-center gap-2 mb-4">
                  <Building2 className="w-4 h-4 text-teal-400" />
                  <h3 className="text-sm font-semibold text-white">Directory At a Glance</h3>
                </div>
                <ul className="space-y-3">
                  <li className="flex items-center justify-between p-3 rounded-xl bg-slate-950/50 border border-slate-800">
                    <div className="flex items-center gap-3">
                      <Users className="w-4 h-4 text-teal-400" />
                      <div>
                        <p className="text-xs text-slate-300">Active Staff</p>
                        <p className="text-[10px] text-slate-500">of {data.counts.total_staff} total</p>
                      </div>
                    </div>
                    <span className="text-lg font-bold text-white">{data.counts.active_staff}</span>
                  </li>
                  <li className="flex items-center justify-between p-3 rounded-xl bg-slate-950/50 border border-slate-800">
                    <div className="flex items-center gap-3">
                      <Stethoscope className="w-4 h-4 text-sky-400" />
                      <div>
                        <p className="text-xs text-slate-300">Referral Doctors</p>
                        <p className="text-[10px] text-slate-500">of {data.counts.total_doctors} total</p>
                      </div>
                    </div>
                    <span className="text-lg font-bold text-white">{data.counts.active_doctors}</span>
                  </li>
                  <li className="flex items-center justify-between p-3 rounded-xl bg-slate-950/50 border border-slate-800">
                    <div className="flex items-center gap-3">
                      <Megaphone className="w-4 h-4 text-indigo-400" />
                      <div>
                        <p className="text-xs text-slate-300">Marketing Execs</p>
                      </div>
                    </div>
                    <span className="text-lg font-bold text-white">{data.counts.active_executives}</span>
                  </li>
                  <li className="flex items-center justify-between p-3 rounded-xl bg-slate-950/50 border border-slate-800">
                    <div className="flex items-center gap-3">
                      <Stethoscope className="w-4 h-4 text-fuchsia-400" />
                      <div>
                        <p className="text-xs text-slate-300">In-house Consultants</p>
                      </div>
                    </div>
                    <span className="text-lg font-bold text-white">{data.counts.active_consultants}</span>
                  </li>
                </ul>

                {!isManagement && (
                  <button
                    onClick={() => navigate('/staff/referrals/new')}
                    data-testid="dashboard-cta-new-referral"
                    className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-teal-500 to-emerald-500 text-slate-950 font-semibold text-sm hover:from-teal-400 hover:to-emerald-400 transition-colors"
                  >
                    <ArrowUpRight className="w-4 h-4" />
                    Log a New Referral
                  </button>
                )}
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
